import { CloseIcon } from '@/components/layout/icons'
import type { GraphEdge, GraphNode } from '@/types/api'

function nodeLabel(nodes: GraphNode[], id: string): string {
  return nodes.find((n) => n.id === id)?.label ?? id
}

/** User-directed inspector crop — small caps section labels. */
const SECTION_LABEL =
  'text-text-3 mb-1.5 text-[10px] font-medium tracking-wide uppercase'

/** One relationship line: "owns → Asset" or "← ownedBy Customer". */
function RelationshipRow({
  edge,
  isSource,
  nodes,
  onSelect,
}: {
  edge: GraphEdge
  isSource: boolean
  nodes: GraphNode[]
  onSelect: (id: string) => void
}) {
  const otherId = isSource ? edge.to : edge.from
  return (
    <button
      type="button"
      onClick={() => onSelect(otherId)}
      className="hover:bg-wash -mx-1 flex w-full min-w-0 items-baseline gap-1.5 rounded-[6px] px-1 py-0.5 text-left text-[13px]"
    >
      <span className="text-text-3">{isSource ? '→' : '←'}</span>
      <span className="text-text-2">{edge.type.replaceAll('_', ' ')}</span>
      <span className="text-text min-w-0 truncate font-medium">
        {nodeLabel(nodes, otherId)}
      </span>
    </button>
  )
}

export function GraphDetailPanel({
  node,
  nodes,
  edges,
  domainColor,
  onSelectNode,
  onClose,
}: {
  node: GraphNode
  nodes: GraphNode[]
  edges: GraphEdge[]
  domainColor: (domain: string) => string
  onSelectNode: (id: string) => void
  onClose: () => void
}) {
  const outgoing = edges.filter((e) => e.from === node.id)
  const incoming = edges.filter((e) => e.to === node.id && e.from !== node.id)

  return (
    <div className="bg-card flex h-full min-h-0 w-full flex-col overflow-y-auto lg:w-[340px] lg:flex-none">
      <div className="flex items-start gap-2.5 px-5 pt-4 pb-3">
        <span
          aria-hidden
          className="mt-1.5 size-2 flex-none rounded-full"
          style={{ background: domainColor(node.domain) }}
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-text text-[15px] font-semibold break-words">
            {node.label}
          </h3>
          <p className="text-text-3 mt-0.5 text-[12px]">{node.domain}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="text-text-3 hover:bg-wash hover:text-text -mt-1 grid size-7 flex-none place-items-center rounded-[8px]"
        >
          <CloseIcon size={16} />
        </button>
      </div>

      <div className="flex flex-col gap-5 px-5 pb-5">
        {node.description && (
          <p className="text-text-2 text-[13px] leading-relaxed">
            {node.description}
          </p>
        )}

        {node.source_tables.length > 0 && (
          <div>
            <div className={SECTION_LABEL}>Source tables</div>
            <p className="text-text-2 font-mono text-[12px] break-all">
              {node.source_tables.join(', ')}
            </p>
          </div>
        )}

        {node.members.length > 0 && (
          <div>
            <div className={SECTION_LABEL}>Members</div>
            <div className="text-text text-[13px] leading-relaxed">
              {node.members.join(', ')}
            </div>
          </div>
        )}

        {(outgoing.length > 0 || incoming.length > 0) && (
          <div>
            <div className={SECTION_LABEL}>Relationships</div>
            <div className="flex flex-col">
              {outgoing.map((e, i) => (
                <RelationshipRow
                  key={`out-${i}`}
                  edge={e}
                  isSource
                  nodes={nodes}
                  onSelect={onSelectNode}
                />
              ))}
              {incoming.map((e, i) => (
                <RelationshipRow
                  key={`in-${i}`}
                  edge={e}
                  isSource={false}
                  nodes={nodes}
                  onSelect={onSelectNode}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
